import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Brand } from '../components/Brand.js';
import { api, ApiError } from '../lib/api.js';
import { trackEvent } from '../lib/analytics.js';

export function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (step === 'email') {
        await api('/v1/auth/request-code', { method: 'POST', body: JSON.stringify({ email }) });
        setStep('code');
      } else {
        let installationId = localStorage.getItem('bpl_installation_id');
        if (!installationId) {
          installationId = crypto.randomUUID();
          localStorage.setItem('bpl_installation_id', installationId);
        }
        await api('/v1/auth/verify-code', {
          method: 'POST',
          body: JSON.stringify({
            email,
            code,
            installationId,
            platform: 'web',
            appVersion: 'web-1.0',
            osVersion: navigator.platform || 'web',
          }),
        });
        trackEvent('signin_completed');
        const requestedReturn = searchParams.get('returnTo');
        navigate(requestedReturn?.startsWith('/app') ? requestedReturn : '/app');
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'We could not reach the server. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-layout">
      <div className="auth-brand">
        <Brand />
        <Link to="/">Back to overview</Link>
      </div>
      <section className="auth-panel">
        <p className="eyebrow">Cyclist account</p>
        <h1>{step === 'email' ? 'Sign in to your trips.' : 'Check your email.'}</h1>
        <p>
          {step === 'email'
            ? 'We will send a six-digit sign-in code. No password to remember.'
            : `Enter the six-digit code sent to ${email}.`}
        </p>
        {searchParams.get('returnTo') === '/app/trips/new' && step === 'email' && (
          <div className="saved-draft-note">
            <strong>Your trip draft is safe.</strong>
            <span>
              Sign in so it can be saved to your account and opened on your other devices.
            </span>
          </div>
        )}
        <form onSubmit={submit}>
          {step === 'email' ? (
            <label>
              Email address
              <input
                autoFocus
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </label>
          ) : (
            <label>
              Six-digit code
              <input
                autoFocus
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                placeholder="000000"
              />
            </label>
          )}
          {error && (
            <p className="inline-error" role="alert">
              {error}
            </p>
          )}
          <button className="button button--wide" disabled={busy}>
            {busy ? 'Please wait…' : step === 'email' ? 'Send sign-in code' : 'Sign in'}
          </button>
        </form>
        {step === 'code' && (
          <button
            className="text-button"
            onClick={() => {
              setStep('email');
              setCode('');
            }}
          >
            Use a different email
          </button>
        )}
        <p className="legal-note">
          By continuing, you agree to the{' '}
          <a href="https://ahmed.wtf/bikepacking-live/terms/">Terms</a> and acknowledge the{' '}
          <a href="https://ahmed.wtf/bikepacking-live/privacy/">Privacy notice</a>.
        </p>
      </section>
      <aside>
        <div className="auth-route" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </div>
        <blockquote>“Live when connected. Saved when not.”</blockquote>
        <p>
          The browser manages your journeys. Reliable background tracking happens in the iOS or
          Android app.
        </p>
      </aside>
    </main>
  );
}
